import { GraphQLResult } from '@aws-amplify/api';
import {
  CreateOrUpdateJobMatchMutation,
  InactiveMatchReason,
  JobMatchInput,
  JobMatchStatus,
  ListJobMatchesByEngineerQuery,
  OpenPosition,
} from '../API';
import callGraphQL from '../graphql-wrapper';
import { createOrUpdateJobMatch } from '../graphql/mutations';
import { notEmpty } from '../lib/functional';
import { mapOpenPosition, OpenPosition as OpenPositionJobs } from './jobs';

export interface JobMatch {
  id: string;
  engineerId: string;
  openPositionId: string;
  openPosition: OpenPositionJobs;
  engineerInterest?: boolean;
  status?: JobMatchStatus;
  inactiveMatchReason?: InactiveMatchReason;
}

function mapJobMatch(jobMatch: NonNullable<CreateOrUpdateJobMatchMutation['createOrUpdateJobMatch']>) {
  const { openPosition } = jobMatch;
  return {
    ...jobMatch,
    openPosition: mapOpenPosition(openPosition as OpenPosition),
  } as JobMatch;
}

function mapJobMatchCreator(result: GraphQLResult<CreateOrUpdateJobMatchMutation>) {
  if (!result.data?.createOrUpdateJobMatch) return null;
  return mapJobMatch(result.data?.createOrUpdateJobMatch);
}

function createOrUpdateJobMatchWrapper(input: JobMatchInput) {
  return callGraphQL<CreateOrUpdateJobMatchMutation>(createOrUpdateJobMatch, {
    variables: { input },
  }).then(mapJobMatchCreator);
}

type ListJobMatchesByEngineerQuery2 = Omit<ListJobMatchesByEngineerQuery, 'listJobMatchesByEngineer'> & {
  listJobMatchesByEngineer?: Omit<ListJobMatchesByEngineerQuery['listJobMatchesByEngineer'], 'items'> & {
    items?: (Omit<NonNullable<NonNullable<NonNullable<ListJobMatchesByEngineerQuery['listJobMatchesByEngineer']>['items']>[0]>, 'openPosition'> & {
      openPosition: NonNullable<CreateOrUpdateJobMatchMutation['createOrUpdateJobMatch']>['openPosition'],
    })[],
  },
};

const listQuery = /* GraphQL */ `
  query ListJobMatchesByEngineer(
    $engineerId: ID
    $openPositionId: ModelIDKeyConditionInput
    $sortDirection: ModelSortDirection
    $filter: ModelJobMatchFilterInput
    $limit: Int
    $nextToken: String
  ) {
    listJobMatchesByEngineer(
      engineerId: $engineerId
      openPositionId: $openPositionId
      sortDirection: $sortDirection
      filter: $filter
      limit: $limit
      nextToken: $nextToken
    ) {
      items {
        id
        engineerId
        openPositionId
        engineerInterest
        status
        inactiveMatchReason
        openPosition {
          id
          name
          titleType
          skills {
            items {
              skillId
            }
          }
          company {
            id
            name
            url
            logoUrl
            summary
          }
          role
          active
          experience
          createdAt
          updatedAt
        }
        createdAt
        updatedAt
      }
      nextToken
    }
  }
`;

function listJobMatchesByEngineerWrapper(engineerId: string, onlyInterested: boolean = false) {
  return callGraphQL<ListJobMatchesByEngineerQuery2>(listQuery, {
    variables: {
      engineerId,
      ...(onlyInterested && { filter: { engineerInterest: { eq: true } } }),
    },
  }).then((result) => {
    const items = result.data?.listJobMatchesByEngineer?.items || [];
    return items.filter(notEmpty).map(mapJobMatch);
  });
}

const listActiveJobMatcheIdsByEngineer = /* GraphQL */ `
  query ListActiveJobMatcheIdsByEngineer(
    $engineerId: ID
  ) {
    listJobMatchesByEngineer(
      engineerId: $engineerId
      filter: {status: {ne: NO_GO}}
    ) {
      items {
        id
      }
    }
  }
`;

export function hasActiveMatches(engineerId: string) {
  return callGraphQL<ListJobMatchesByEngineerQuery>(listActiveJobMatcheIdsByEngineer, {
    variables: { engineerId },
  }).then((result) => Boolean(result?.data?.listJobMatchesByEngineer?.items?.length));
}

export {
  createOrUpdateJobMatchWrapper as createOrUpdateJobMatch,
  listJobMatchesByEngineerWrapper as listJobMatchesByEngineer,
};
